/* Added by Wilson from Galadrim */

(() => {
  "use strict";

  // --- UTILITY: Debounce function to delay execution ---
  const debounce = (callback, wait) => {
    let timeoutId = null;
    return (...args) => {
      window.clearTimeout(timeoutId);
      timeoutId = window.setTimeout(() => {
        callback.apply(null, args);
      }, wait);
    };
  };

  // --- CORE LOGIC: The single, reliable function to update the cart and refresh the UI ---
  async function updateCartAndRefresh(variantId, newQty) {
    const cartContainer = document.querySelector(".cartitems--container");
    if (cartContainer) cartContainer.classList.add("is-updating");

    try {
      // Get cart item data from the DOM
      const cartItem = document.querySelector(`[data-variant="${variantId}"]`);
      if (!cartItem) {
        throw new Error(`Cart item not found for variant ${variantId}`);
      }

      const itemKey = cartItem.getAttribute("data-cartitem-key");

      // Use Empire theme's cart update pattern with FormData
      const formData = new FormData();
      formData.append("updates[" + itemKey + "]", newQty);

      const response = await fetch("/cart/update.js", {
        method: "POST",
        body: formData,
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status} from cart API`);
      }

      const cart = await response.json();

      // Update the cart item data attributes
      cartItem.setAttribute("data-quantity", newQty);

      if (cartItem) {
        const priceElement = cartItem.querySelector(".cart-item--price");
        if (priceElement) {
          const updatedItem = cart.items.find(item => item.variant_id == variantId);
          if (updatedItem) {
            priceElement.textContent = (updatedItem.final_line_price / 100).toFixed(2).replace(".", ",") + " €";
          }
        }

        // const discountEl = cartItem.querySelector('.discount-list');
        // if(discountEl){
        //   // discountEl.querySelectorAll('*').forEacch(el => el.remove());
        // }

        // IMPROVEMENT: Refresh ALL promotion containers for mix-and-match promos
        const allPromoContainers = document.querySelectorAll(".custom-promotions-container");
        allPromoContainers.forEach(container => {
          document.dispatchEvent(
            new CustomEvent("promos:refresh-single", {
              detail: { container: container, variantId: variantId },
            })
          );
        });
      }

      // IMPROVEMENT: Update cart summary and header badge
      if (typeof window.updateCartSummary === "function") {
        window.updateCartSummary();
      }

      // Update header cart badge using Empire theme pattern
      const countEvent = new CustomEvent("cartcount:update", {
        detail: cart,
      });
      window.dispatchEvent(countEvent);

      // Dispatch cart:updated event to notify threshold-bar and other components
      document.dispatchEvent(
        new CustomEvent("cart:updated", {
          detail: { cart },
        })
      );
    } catch (err) {
      console.error("[CartQty] Cart update failed:", err);
      if (typeof window.refreshCartPageSection === "function") {
        await window.refreshCartPageSection();
        document.dispatchEvent(new Event("promos:refresh"));
      } else {
        if (cartContainer) cartContainer.classList.remove("is-updating");
      }
    } finally {
      if (cartContainer) cartContainer.classList.remove("is-updating");
    }
  }

  // Create debounced versions for both text input and button clicks
  const debouncedUpdate = debounce(updateCartAndRefresh, 650);
  const debouncedButtonUpdate = debounce(updateCartAndRefresh, 300); // Plus rapide pour les boutons

  // --- EVENT ATTACHMENT ---
  function attachCartPageHandlers() {
    const container = document.querySelector(".cartitems--container");
    if (!container || container.hasAttribute("data-custom-cart-handlers-v3")) return;
    container.setAttribute("data-custom-cart-handlers-v3", "true");

    // 1. Handler for +/- button clicks (now uses debounce)
    container.addEventListener(
      "click",
      e => {
        const plus = e.target.closest("[data-quantity-plus]");
        const minus = e.target.closest("[data-quantity-minus]");
        if (!plus && !minus) return;

        e.preventDefault();
        e.stopImmediatePropagation();

        const selector = e.target.closest("[data-quantity-selector]");
        const input = selector?.querySelector(".quantity-selector__input");
        if (!input) return;

        // PATCH: Check if input is already being updated
        if (input.hasAttribute("data-updating")) return;

        const min = 1;
        const max = parseInt(input.getAttribute("max"), 10) || Infinity;
        const originalQty = parseInt(input.value, 10) || 1;
        let newQty = originalQty;

        if (plus && originalQty >= max) {
          const errorMsg = selector.querySelector("[data-quantity-error-message]");
          const wrapper = selector.closest(".cart-item--qty-block");
          if (errorMsg && wrapper) {
            // Vérifier si le message n'est pas déjà affiché pour éviter les conflits
            if (!errorMsg.classList.contains("show")) {
              errorMsg.classList.add("show");
              wrapper.classList.add("has-active-error");
              setTimeout(() => {
                errorMsg.classList.remove("show");
                wrapper.classList.remove("has-active-error");
              }, 3000);
            }
          }
          return;
        }

        if (plus && newQty < max) newQty += 1;
        if (minus && newQty > min) newQty -= 1;

        // IMPROVEMENT: Also check if the new quantity would exceed max
        if (plus && newQty > max) {
          const errorMsg = selector.querySelector("[data-quantity-error-message]");
          const wrapper = selector.closest(".cart-item--qty-block");
          if (errorMsg && wrapper) {
            if (!errorMsg.classList.contains("show")) {
              errorMsg.classList.add("show");
              wrapper.classList.add("has-active-error");
              setTimeout(() => {
                errorMsg.classList.remove("show");
                wrapper.classList.remove("has-active-error");
              }, 3000);
            }
          }
          return;
        }

        if (newQty === originalQty) return;

        // PATCH: Mark input as updating
        input.setAttribute("data-updating", "true");
        input.value = newQty;

        const row = input.closest(".cart-item");
        const variantId = row?.getAttribute("data-variant");
        if (variantId) {
          // Utiliser le debounce pour les boutons aussi
          debouncedButtonUpdate(variantId, newQty);
          // Remove updating flag after a short delay
          setTimeout(() => {
            input.removeAttribute("data-updating");
          }, 350); // Slightly longer than debounce delay
        } else {
          input.removeAttribute("data-updating");
        }
      },
      true
    );

    // 2. Handler for direct text input (triggers update after a short delay)
    container.addEventListener("input", e => {
      const input = e.target;
      if (!input.matches(".quantity-selector__input")) return;

      // PATCH: Check if input is already being updated
      if (input.hasAttribute("data-updating")) return;

      const min = 1;
      const max = parseInt(input.getAttribute("max"), 10) || Infinity;
      let value = parseInt(input.value, 10);

      if (isNaN(value)) return;

      if (value > max) {
        input.value = max;
        value = max;
      }

      const row = input.closest(".cart-item");
      const variantId = row?.getAttribute("data-variant");
      if (variantId && value >= min) {
        // PATCH: Mark input as updating
        input.setAttribute("data-updating", "true");
        debouncedUpdate(variantId, value);
        // Remove updating flag after a short delay
        setTimeout(() => {
          input.removeAttribute("data-updating");
        }, 700); // Slightly longer than debounce delay
      }
    });
  }

  // --- INITIALIZATION ---
  function initialize() {
    if (document.body.classList.contains("template-cart")) {
      attachCartPageHandlers();
    }
  }

  // Try to initialize immediately if DOM is already loaded
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initialize);
  } else {
    initialize();
  }

  document.addEventListener("shopify:section:load", e => {
    if (e.target.querySelector && e.target.querySelector(".cartitems--container")) {
      initialize();
    }
  });
  document.addEventListener("cart:updated", initialize);
})();
